import { ChartColumnIncreasing, ChevronRight } from 'lucide-react';
import { cn, type Column } from '@eombor/ui';
import type { FlowLevel, FlowRow } from '@eombor/contracts';
import { t } from '../../../shared/i18n/index.js';
import { ResponsiveTable } from '../../../shared/ui/ResponsiveTable.js';
import type { FlowNode } from '../api/flow.api.js';

/**
 * Bitta darajaning jadvali.
 *
 * Satr BUTUNLAY bosiladigan qilinmadi (`onRowClick`): u jadvaldagi HAR
 * qatorni fokus oladigan qilib qo'yadi, pastga yo'li yo'q satrlar esa
 * klaviatura bilan yurganda hech narsa qilmaydigan to'xtash joyiga
 * aylanardi. Shuning uchun nishon — nomning O'ZI, va u faqat kirish
 * mumkin bo'lganda tugma bo'ladi.
 */

const format = (value: number): string => value.toLocaleString('ru-RU');

/** Ustun sarlavhasi darajaga qarab o'zgaradi — «Korxona», «Bo'linma»... */
const NAME_HEADER: Record<FlowLevel, string> = {
  MTU: 'stats.levelMtu',
  ENTERPRISE: 'stats.levelEnterprise',
  SUBDIVISION: 'stats.levelSubdivision',
  STOREROOM: 'stats.levelStoreroom',
};

/**
 * «3 korxona», «2 bo'linma» — pastda nima turganini AYTADI.
 *
 * `MTU` yo'q va bo'lmaydi: bu jadval QUYI darajaning nomini oladi, MTU
 * esa hech qachon quyi daraja bo'lmaydi — eng yuqorisi o'sha.
 */
const KIDS_KEY: Partial<Record<FlowLevel, string>> = {
  ENTERPRISE: 'stats.kidsEnterprise',
  SUBDIVISION: 'stats.kidsSubdivision',
  STOREROOM: 'stats.kidsStoreroom',
};

export interface FlowTableProps {
  level: FlowLevel;
  rows: readonly FlowRow[];
  onDrill: (node: FlowNode) => void;
  /** Satrning O'ZI haqida batafsil — panel ochiladi. */
  onDetail: (row: FlowRow) => void;
}

export const FlowTable = ({ level, rows, onDrill, onDetail }: FlowTableProps) => {
  const columns: Column<FlowRow>[] = [
    {
      key: 'name',
      header: t(NAME_HEADER[level]),
      sticky: 'left',
      render: (row) => {
        const kidsKey = row.drillLevel === null ? undefined : KIDS_KEY[row.drillLevel];
        /* Ajratgich faqat IKKI tomoni ham bo'lganda: aks holda satr ostida
           osilib qolgan «·» turardi. */
        const subtitle = [
          row.code,
          kidsKey === undefined ? null : t(kidsKey, { count: row.childCount }),
        ]
          .filter((part): part is string => part !== null && part !== '')
          .join(' · ');

        const body = (
          <>
            <span className="truncate font-medium text-text">{row.name}</span>
            {subtitle === '' ? null : (
              <span className="truncate text-xs text-text-faint">{subtitle}</span>
            )}
          </>
        );

        if (row.drillLevel === null) {
          return <span className="flex min-w-0 flex-col">{body}</span>;
        }

        return (
          <button
            type="button"
            onClick={() => onDrill({ level: row.drillLevel as FlowLevel, parentId: row.id })}
            aria-label={t('stats.open', { name: row.name })}
            className={cn(
              'flex min-w-0 items-center gap-1.5 text-left',
              'rounded-[var(--radius-sm)] px-1 py-0.5 -mx-1',
              'transition-colors duration-[var(--motion-fast)]',
              'hover:bg-accent-soft/50',
              'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent',
            )}
          >
            <span className="flex min-w-0 flex-col">{body}</span>
            {/* Strelka tugmaning ICHIDA: alohida ustunda turganida u
                bosilmaydigan bezak edi va «bosib ko'rdim, ishlamadi»
                degan savol tug'dirardi. */}
            <ChevronRight size={15} aria-hidden className="shrink-0 text-text-faint" />
          </button>
        );
      },
    },
    {
      key: 'inDocCount',
      header: t('stats.colIn'),
      align: 'right',
      mono: true,
      width: '7rem',
      /* Nol KULRANG qoladi: hamma qatorda bir xil bo'yalgan raqam
         o'qilmay qolardi, farq esa aynan nolda ko'rinishi kerak. */
      render: (row) =>
        row.inDocCount === 0 ? (
          <span className="text-text-faint">0</span>
        ) : (
          <span className="font-medium text-success">{format(row.inDocCount)}</span>
        ),
    },
    {
      key: 'outDocCount',
      header: t('stats.colOut'),
      align: 'right',
      mono: true,
      width: '7rem',
      render: (row) =>
        row.outDocCount === 0 ? (
          <span className="text-text-faint">0</span>
        ) : (
          <span className="font-medium text-info">{format(row.outDocCount)}</span>
        ),
    },
    {
      key: 'lotCount',
      header: t('stats.colLots'),
      align: 'right',
      mono: true,
      width: '9rem',
      render: (row) =>
        row.lotCount === 0 ? (
          <span className="text-text-faint">0</span>
        ) : (
          format(row.lotCount)
        ),
    },
    {
      key: 'detail',
      header: '',
      align: 'right',
      width: '3.5rem',
      /*
       * BATAFSIL — nomdagi tugmadan BOSHQA ish qiladi.
       *
       * Nom pastga tushiradi («qaysi bo'linmalar?»), bu esa shu
       * tugunning o'zi haqida gapiradi («nima kirdi, kim oldi?»).
       * Ikkovini bitta tugmaga sig'dirib bo'lmaydi va ulardan birini
       * yashirish rahbarni ikkinchi savol uchun boshqa bo'limga
       * yuborardi.
       *
       * Pastga yo'li yo'q satrda esa aynan SHU tugma yagona nishon
       * bo'ladi — ya'ni «bo'linmasi yo'q korxona» ham o'z raqamlarini
       * ko'rsatadi.
       */
      render: (row) => (
        <button
          type="button"
          onClick={() => onDetail(row)}
          aria-label={t('stats.detailOf', { name: row.name })}
          className={cn(
            'inline-flex size-8 items-center justify-center',
            'rounded-[var(--radius-sm)] text-text-faint',
            'transition-colors duration-[var(--motion-fast)]',
            'hover:bg-surface-2 hover:text-text',
            'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent',
          )}
        >
          <ChartColumnIncreasing size={16} aria-hidden />
        </button>
      ),
    },
  ];

  return (
    <ResponsiveTable
      columns={columns}
      rows={[...rows]}
      rowKey={(row) => `${row.id}:${row.name}`}
      density="compact"
      caption={t('stats.tableTitle')}
      /* Telefonda batafsil tugmasi kartochkaning AMALI bo'lib,
         pastda butun enni oladi — barmoq nishoni 44px. */
      card={{ title: 'name', actions: 'detail' }}
    />
  );
};
